import React from 'react';

/** The shapes a skeleton can take: one per kind of content whose footprint is known. */
export const SKELETON_VARIANTS = Object.freeze([
	'line',
	'block',
	'avatar',
	'list-row',
	'table-row',
	'tile',
	'form-field',
	'chart-frame',
]);

// A page that asks for a thousand rows gets two hundred: a placeholder is a
// hint at the shape, and a runaway count must not freeze the render.
export const SKELETON_MAX_COUNT = 200;

const px = (value) => (typeof value === 'number' ? `${value}px` : value);

const Bar = ({ className = '', style }) => (
	<span className={`skeleton ${className}`.trim()} style={style} />
);

function Unit({ variant, index, count, width, height, size, columns }) {
	switch (variant) {
		case 'line':
			// The last line of a stack is shorter, the way a paragraph ends.
			return (
				<Bar
					className='skeleton--line'
					style={{ width: width != null ? px(width) : index === count - 1 && count > 1 ? '60%' : '100%' }}
				/>
			);
		case 'block':
			return <Bar className='skeleton--block' style={{ width: px(width), height: px(height) }} />;
		case 'avatar':
			return <Bar className='skeleton--avatar' style={{ width: px(size), height: px(size) }} />;
		case 'list-row':
			return (
				<span className='skeleton-row'>
					<Bar className='skeleton--avatar skeleton--avatar-tile' />
					<span className='skeleton-row__text'>
						<Bar className='skeleton--line' style={{ width: '42%' }} />
						<Bar className='skeleton--line skeleton--line-short' style={{ width: '26%' }} />
					</span>
				</span>
			);
		case 'table-row':
			return (
				<span className='skeleton-table-row'>
					{Array.from({ length: columns }, (_, cell) => (
						<Bar key={cell} className='skeleton--line' style={{ width: cell === 0 ? '70%' : '48%' }} />
					))}
				</span>
			);
		case 'tile':
			return (
				<span className='skeleton-tile'>
					<span className='skeleton-tile__head'>
						<Bar className='skeleton--avatar skeleton--avatar-tile' style={{ width: 28, height: 28 }} />
						<Bar className='skeleton--line' style={{ width: '45%' }} />
					</span>
					<Bar className='skeleton--line' />
					<Bar className='skeleton--line' style={{ width: '70%' }} />
				</span>
			);
		case 'form-field':
			return (
				<span className='skeleton-field'>
					<Bar className='skeleton--label' />
					<Bar className='skeleton--input' />
				</span>
			);
		case 'chart-frame':
			return (
				<span className='skeleton-chart'>
					<Bar className='skeleton--line' style={{ width: '36%' }} />
					<Bar className='skeleton--block skeleton--chart-plot' style={{ height: px(height) }} />
				</span>
			);
		default:
			return null;
	}
}

/**
 * The placeholder for content whose shape is known (docs/platform-standards.md
 * §14). Generic per surface type, not per field: pick the variant that matches
 * what is loading and it occupies the same footprint the content will, so
 * nothing moves when the data arrives.
 *
 *   <Skeleton variant='list-row' count={6} />
 *   <Skeleton variant='form-field' count={4} />
 *   <Skeleton variant='chart-frame' />
 *
 * Every skeleton is decoration: the wrapper is `aria-hidden`, so assistive
 * technology never reads a wall of empty shapes. The region that owns the
 * wait carries `aria-busy` and the live announcement (see DataState).
 *
 * Colors are the `--skeleton-base` / `--skeleton-highlight` tokens, never an
 * accent. The shimmer is CSS and stops under `prefers-reduced-motion`.
 *
 * `count` repeats the shape (a stack of rows); it is clamped to
 * SKELETON_MAX_COUNT and 0 or less renders nothing. `width`/`height` size the
 * `line` and `block`/`chart-frame` shapes, `size` the `avatar`, `columns` the
 * `table-row`.
 */
export default function Skeleton({
	variant = 'line',
	count = 1,
	width,
	height,
	size = 32,
	columns = 4,
	className = '',
	...rest
}) {
	const safe = Number.isFinite(count) ? Math.min(Math.floor(count), SKELETON_MAX_COUNT) : 1;
	if (safe <= 0) return null;
	if (!SKELETON_VARIANTS.includes(variant)) return null;
	return (
		<div
			className={`skeleton-stack skeleton-stack--${variant}${className ? ` ${className}` : ''}`}
			aria-hidden='true'
			data-variant={variant}
			{...rest}
		>
			{Array.from({ length: safe }, (_, index) => (
				<Unit
					key={index}
					variant={variant}
					index={index}
					count={safe}
					width={width}
					height={height}
					size={size}
					columns={columns}
				/>
			))}
		</div>
	);
}
